import {
  addDays,
  addHours,
  startOfDay,
  isBefore,
  isAfter,
  format,
} from "date-fns";
import { generateTimeSlots, type OpeningHours } from "./bookingUtils";
import { Json } from "@/integrations/supabase/types";
import { Product } from "@/components/product-step/types";
import { getUrlParam } from "./urlUtils";
import {
  getExternalSlotsForDate,
  hasExternalSlots,
} from "@/hooks/useClinicExternalSlots";

// External slot type for clinics with external booking systems
export interface ExternalSlot {
  slot_date: string;
  times: string[];
  fetched_at: string;
}

// Helper function to check if internal mode is enabled
const getIsInternalMode = (): boolean => {
  return getUrlParam("internal") === "true";
};

// Parse a YYYY-MM-DD string as a local-date midnight (avoids UTC timezone shifts)
const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

// Helper function to check if a date is in the holiday period (Dec 19-31)
const isHolidayPeriod = (date: Date): boolean => {
  const month = date.getMonth(); // 0-11
  const day = date.getDate(); // 1-31

  // December 19-31 (month = 11, day >= 19)
  return month === 11 && day >= 19;
};

// Helper function to determine if this is a blood-only booking
const isBloodOnlyBooking = (selectedProductDetails?: Product[]): boolean => {
  if (!selectedProductDetails || selectedProductDetails.length === 0) {
    return false;
  }

  // Check if all selected products support blood-only and don't include MRI
  return selectedProductDetails.every((product) => {
    const packageTypes = product.package_types || [];
    return packageTypes.includes("blood-only") && !packageTypes.includes("mri");
  });
};

// Helper function to get max consultation delay days from products and add-ons
const getMaxConsultationDelayDays = (
  selectedProductDetails?: Product[],
  selectedAddOnDetails?: Product[],
): number => {
  const allProducts = [
    ...(selectedProductDetails || []),
    ...(selectedAddOnDetails || []),
  ];

  if (allProducts.length === 0) {
    return 0;
  }

  // Get the maximum requires_consultation_beforehand value
  const maxDays = Math.max(
    0,
    ...allProducts.map(
      (product) => product.requires_consultation_beforehand || 0,
    ),
  );

  return maxDays;
};

export const getFirstAvailableDate = (
  selectedClinic?: Clinic | null,
  selectedProductDetails?: Product[],
  selectedAddOnDetails?: Product[],
  externalSlots?: ExternalSlot[] | null,
) => {
  const now = new Date();
  const today = startOfDay(now);
  const isInternal = getIsInternalMode();

  // For internal bookings, allow booking from today with no restrictions
  if (isInternal) {
    return today;
  }

  // Blood-only bookings require 2 business days, others require 3
  // Holiday period (Dec 19-31) requires 4 business days
  let businessDaysOffset = isBloodOnlyBooking(selectedProductDetails) ? 2 : 3;
  if (isHolidayPeriod(today)) {
    businessDaysOffset = 4;
  }

  // Get max consultation delay and add to baseline offset
  const maxConsultationDelayDays = getMaxConsultationDelayDays(
    selectedProductDetails,
    selectedAddOnDetails,
  );
  const totalBusinessDaysOffset = businessDaysOffset + maxConsultationDelayDays;

  if (maxConsultationDelayDays > 0) {
    console.log(
      `Consultation required: max ${maxConsultationDelayDays} days added to baseline ${businessDaysOffset} days = ${totalBusinessDaysOffset} days total`,
    );
  }

  const baseAvailableDate = addBusinessDays(today, totalBusinessDaysOffset);

  let fromDate = baseAvailableDate;

  // Consider clinic's bookable_from date if available
  if (selectedClinic?.bookable_from) {
    try {
      const bookableFrom = parseLocalDate(selectedClinic.bookable_from);
      fromDate = isAfter(bookableFrom, baseAvailableDate)
        ? bookableFrom
        : baseAvailableDate;
    } catch (error) {
      console.error("Error parsing bookable_from date:", error);
    }
  }

  // Function to check if a date has any available slots
  const hasAvailableSlots = (date: Date) => {
    // If clinic has external slots, use those
    const externalSlotsForDate = getExternalSlotsForDate(
      externalSlots,
      date,
      selectedClinic?.clinic_closed_times,
    );
    if (externalSlotsForDate !== null) {
      // External slots found - use them (empty array means no slots)
      return externalSlotsForDate.length > 0;
    }
    // A clinic that books only what its partner publishes has nothing on a
    // day the sync does not cover (past its window): no opening-hours guess.
    if (
      hasExternalSlots(externalSlots) &&
      !selectedClinic?.accepts_request_slots
    ) {
      return false;
    }

    // Fall back to opening hours
    const isWeekend = [0, 6].includes(date.getDay());
    const slots = generateTimeSlots(
      date,
      isWeekend,
      selectedClinic?.opening_hours as OpeningHours,
      isInternal,
      selectedClinic?.clinic_closed_times,
    );
    return slots.length > 0;
  };

  // Find the first date with available slots starting from fromDate
  // Limit search to prevent infinite loop
  let checkDate = fromDate;
  const maxIterations = 365;
  let iterations = 0;

  while (!hasAvailableSlots(checkDate) && iterations < maxIterations) {
    checkDate = addDays(checkDate, 1);
    iterations++;
  }

  return checkDate;
};

export interface Clinic {
  opening_hours: Json;
  bookable_from?: string | null;
  // Whether the clinic takes bookings at times its partner never published;
  // when false its synced slots are the whole offer (see bookableSlots.ts).
  accepts_request_slots?: boolean;
  id?: string;
  name?: string;
  status?: string;
  clinic_closed_times?: Array<{
    id: string;
    clinic_id: string;
    closed_date: string;
    time_range: string | null;
    comment: string | null;
  }>;
}

export const isDateDisabled = (
  date: Date,
  selectedClinic?: Clinic | null,
  selectedProductDetails?: Product[],
  selectedAddOnDetails?: Product[],
  externalSlots?: ExternalSlot[] | null,
) => {
  const now = new Date();
  const today = startOfDay(now);
  const isInternal = getIsInternalMode();

  // For internal bookings, only check if date is in the future
  if (isInternal) {
    // Allow any date from today onwards
    if (isBefore(date, today)) {
      return true; // Date is in the past
    }
    // For internal bookings, still generate time slots to check if any are available
    const isWeekendDay = isWeekend(date);
    const slots = generateTimeSlots(
      date,
      isWeekendDay,
      undefined,
      isInternal,
      selectedClinic?.clinic_closed_times,
    );
    return slots.length === 0;
  }

  // Blood-only bookings require 2 business days, others require 3
  // Holiday period (Dec 19-31) requires 4 business days
  let businessDaysOffset = isBloodOnlyBooking(selectedProductDetails) ? 2 : 3;
  if (isHolidayPeriod(today)) {
    businessDaysOffset = 4;
  }

  // Get max consultation delay and add to baseline offset
  const maxConsultationDelayDays = getMaxConsultationDelayDays(
    selectedProductDetails,
    selectedAddOnDetails,
  );
  const totalBusinessDaysOffset = businessDaysOffset + maxConsultationDelayDays;

  const baseAvailableDate = addBusinessDays(today, totalBusinessDaysOffset);

  let fromDate = baseAvailableDate;

  if (selectedClinic?.bookable_from) {
    try {
      const bookableFrom = parseLocalDate(selectedClinic.bookable_from);
      fromDate = isAfter(bookableFrom, baseAvailableDate)
        ? bookableFrom
        : baseAvailableDate;
    } catch (error) {
      console.error("Error parsing bookable_from date:", error);
    }
  }

  if (isBefore(date, fromDate)) {
    return true;
  }

  // Check if clinic has external slots - if so, use those for availability
  const externalSlotsForDate = getExternalSlotsForDate(
    externalSlots,
    date,
    selectedClinic?.clinic_closed_times,
  );
  if (externalSlotsForDate !== null) {
    // External slots found for this date - empty array means no slots available
    return externalSlotsForDate.length === 0;
  }
  // A clinic that books only what its partner publishes is closed on a day
  // the sync does not cover (past its window), whatever its opening hours say.
  if (
    hasExternalSlots(externalSlots) &&
    !selectedClinic?.accepts_request_slots
  ) {
    return true;
  }

  // Fall back to opening hours logic for clinics without external slots

  if (selectedClinic?.opening_hours) {
    const day = date.getDay();
    const dayName = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ][day].toLowerCase();

    try {
      let openingHours: OpeningHours = {};

      if (typeof selectedClinic.opening_hours === "string") {
        try {
          openingHours = JSON.parse(selectedClinic.opening_hours);
        } catch (e) {
          console.error("Error parsing opening_hours string:", e);
        }
      } else {
        openingHours = selectedClinic.opening_hours as OpeningHours;
      }

      const dayHours = openingHours[dayName];

      // Check if day is closed or not in opening hours
      if (!dayHours) {
        return true; // Day is not in opening hours
      }

      // Handle both formats: array (with lunch breaks) and object (single time slot)
      if (Array.isArray(dayHours)) {
        // Array format - check if any range exists and is not closed
        if (
          dayHours.length === 0 ||
          dayHours.some((range: { closed?: boolean }) => range.closed)
        ) {
          return true; // All ranges closed or empty array
        }
      } else {
        // Object format - check if closed
        if (dayHours.closed) {
          return true; // Day is closed
        }
      }
    } catch (error) {
      console.error("Error processing opening_hours:", error);
    }
  }

  // Check clinic blacklist for closed dates
  if (
    selectedClinic?.clinic_closed_times &&
    selectedClinic.clinic_closed_times.length > 0
  ) {
    const dateString = format(date, "yyyy-MM-dd");

    for (const closedTime of selectedClinic.clinic_closed_times) {
      if (closedTime.closed_date === dateString) {
        // If no time_range specified, the entire day is closed
        if (!closedTime.time_range) {
          return true;
        }
        // If time_range is specified, only those hours are available (handled in generateTimeSlots)
      }
    }
  }

  const isWeekendDay = isWeekend(date);
  const slots = generateTimeSlots(
    date,
    isWeekendDay,
    selectedClinic?.opening_hours as OpeningHours,
    isInternal,
    selectedClinic?.clinic_closed_times,
  );
  const hasAvailableSlots = slots.length > 0;

  return !hasAvailableSlots;
};

const isWeekend = (date: Date) => {
  const day = date.getDay();
  return day === 0 || day === 6; // 0 is Sunday, 6 is Saturday
};

export const addBusinessDays = (date: Date, days: number) => {
  let result = new Date(date);
  let addedDays = 0;

  while (addedDays < days) {
    result = addDays(result, 1);
    if (!isWeekend(result)) {
      addedDays++;
    }
  }

  return result;
};

export const getNextWeekday = (date: Date) => {
  let nextDay = new Date(date);
  while (isWeekend(nextDay)) {
    nextDay = addDays(nextDay, 1);
  }
  return nextDay;
};

export const getNextAvailableDate = (
  fromDate: Date,
  selectedClinic?: Clinic | null,
  selectedProductDetails?: Product[],
  selectedAddOnDetails?: Product[],
  externalSlots?: ExternalSlot[] | null,
): Date => {
  const isInternal = getIsInternalMode();

  // For internal bookings, don't skip weekends
  const checkFunction = isInternal
    ? (date: Date) => addDays(date, 1)
    : (date: Date) => getNextWeekday(addDays(date, 1));

  let checkDate = isInternal ? fromDate : getNextWeekday(fromDate);
  const maxIterations = 90; // Prevent infinite loop - check up to 90 days ahead
  let iterations = 0;

  while (iterations < maxIterations) {
    if (
      !isDateDisabled(
        checkDate,
        selectedClinic,
        selectedProductDetails,
        selectedAddOnDetails,
        externalSlots,
      )
    ) {
      return checkDate;
    }
    checkDate = checkFunction(checkDate);
    iterations++;
  }

  // Fallback if no available date found within 90 days
  return checkDate;
};
